import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import AuthShell from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, Lock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import PasswordInput from "@/components/PasswordInput";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Le password non coincidono");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password aggiornata");
      setTimeout(() => nav("/login"), 1800);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const fieldCls = "pl-10 h-11 rounded-sm bg-white/[0.04] border-white/[0.1] text-white placeholder:text-white/25 font-geist focus-visible:ring-[#4D8AFF] focus-visible:border-[#4D8AFF]/50";
  const labelCls = "font-geist-mono text-[10px] uppercase tracking-[0.18em] text-white/50";

  if (!token) {
    return (
      <AuthShell step="Errore" title="Token mancante" subtitle="Questo link non è valido o è incompleto.">
        <Link to="/forgot-password" className="text-[#4D8AFF] hover:text-white font-geist-mono text-[11px] uppercase tracking-wider">
          → Richiedi un nuovo link
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      step="03 · Nuova password"
      title="Imposta una nuova password"
      subtitle="Scegli una password sicura. Verrai reindirizzato al login dopo il salvataggio."
    >
      {done ? (
        <div data-testid="reset-success" className="flex items-start gap-3 p-4 border border-emerald-400/25 bg-emerald-400/[0.06] rounded-sm">
          <CheckCircle2 className="h-5 w-5 text-emerald-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-white/80">
            Password aggiornata. Reindirizzamento al login...
          </div>
        </div>
      ) : (
        <form onSubmit={submit} data-testid="reset-form" className="space-y-5">
          <div>
            <Label htmlFor="password" className={labelCls}>Nuova password</Label>
            <div className="relative mt-2">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
              <PasswordInput
                id="password"
                data-testid="reset-password-input"
                toggleTestId="reset-password-toggle"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                placeholder="minimo 6 caratteri"
                className={fieldCls}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="confirm" className={labelCls}>Conferma password</Label>
            <div className="relative mt-2">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
              <PasswordInput
                id="confirm"
                data-testid="reset-confirm-input"
                toggleTestId="reset-confirm-toggle"
                required
                minLength={6}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                placeholder="ripeti la password"
                className={fieldCls}
              />
            </div>
          </div>

          {error && (
            <div
              data-testid="reset-error"
              className="font-geist-mono text-[11px] text-red-300 border border-red-400/30 bg-red-500/10 rounded-sm px-3 py-2"
            >
              {error}
            </div>
          )}

          <Button
            data-testid="reset-submit-btn"
            type="submit"
            disabled={busy}
            className="w-full h-11 rounded-sm bg-[#4D8AFF] hover:bg-[#75A6FF] text-[#050B1F] font-geist font-semibold tracking-tight group transition-colors"
          >
            {busy ? (
              <span className="font-geist-mono text-xs uppercase tracking-wider">Salvataggio...</span>
            ) : (
              <span className="flex items-center gap-2">
                Aggiorna password
                <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
              </span>
            )}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
